import client from "@/apiClient";

// ── Day + meal-plan endpoints ───────────────────────────────────
export const getDay = (voyageId, dayNumber) =>
  client.get(`/voyages/${voyageId}/days/${dayNumber}`).then((r) => r.data);

export const patchDay = (voyageId, dayNumber, body) =>
  client.put(`/voyages/${voyageId}/days/${dayNumber}`, body).then((r) => r.data);

export const putMealPlan = (voyageId, dayNumber, body) =>
  client
    .put(`/voyages/${voyageId}/days/${dayNumber}/meal-plan`, body)
    .then((r) => r.data);

export const eventsFromDefaults = (voyageId, dayNumber) =>
  client
    .post(`/voyages/${voyageId}/days/${dayNumber}/meal-plan/from-defaults`)
    .then((r) => r.data);

// Phase 7+++ — Append meal_plan rows for newly enabled lib restaurants.
// Idempotent. Returns { voyage_id, days_modified, added: {regular,specialty,buffet}, total_added }
export const syncMealPlanFromLibrary = (voyageId) =>
  client
    .post(`/voyages/${voyageId}/meal-plan/sync-from-library`)
    .then((r) => r.data);

export const getFullMealPlan = (voyageId) =>
  client.get(`/voyages/${voyageId}/meal-plan`).then((r) => r.data);

// ── Library fetches (cached at editor level) ────────────────────
export const listRestaurants = () =>
  client.get("/lib/restaurants").then((r) => r.data);

export const listThemeBuffets = () =>
  client.get("/lib/theme_buffets").then((r) => r.data);

export const listLiveStations = () =>
  client.get("/lib/live_stations").then((r) => r.data);

// Phase 7+++ — Standard Stations library (parallel to Live Stations,
// not "live" — pre-set buffet stations like Salad / Bread / Cheese).
export const listStandardStations = () =>
  client.get("/lib/standard_stations").then((r) => r.data);

export const listCrewMessThemes = () =>
  client.get("/lib/crew_mess_themes").then((r) => r.data);

export const listChefsSpecials = () =>
  client.get("/lib/chefs_specials").then((r) => r.data);

export const listSpecialEvents = () =>
  client.get("/lib/special_events").then((r) => r.data);

export const listTargets = () =>
  client.get("/lib/targets").then((r) => r.data);

export const listFestivals = () =>
  client.get("/lib/festivals").then((r) => r.data);

// Phase 7++++ — Buffet Materials library (service-ware tags applied
// per-dish on buffet stations / lines). Endpoint is identical in shape
// to the other library lists.
export const listBuffetMaterials = () =>
  client.get("/lib/buffet_materials").then((r) => r.data);

// Phase 7+/Crew-Mess-Buffet — separate library for crew-mess live
// stations. Identical schema to lib_live_stations; the Live Station
// picker on the crew_mess editor reads from this collection only.
export const listCrewMessLiveStations = () =>
  client.get("/lib/crew_mess_live_stations").then((r) => r.data);

// One-click duplicate a Live Station into the parallel crew-mess
// library. Returns the newly-created crew-mess entry.
export const copyLiveStationToCrewMess = (id) =>
  client.post(`/lib/live_stations/${id}/copy-to-crew-mess`).then((r) => r.data);

// Phase 15 (B1) — in-place duplicate inside the SAME library
// collection. `libraryName` is the URL slug, e.g. "live_stations",
// "crew_mess_live_stations", "canapes", etc. Returns the new doc with
// a fresh id and an auto-suffixed " (Copy)" name.
export const duplicateLibItem = (libraryName, id) =>
  client.post(`/lib/${libraryName}/${id}/duplicate`).then((r) => r.data);

// Phase 15 (F3) — map of {live_station_id: [{day_number, meal, …}]}
// across the voyage. Used to surface a soft yellow toast on save when
// the same Live Station shows up on more than one day.
export const listVoyageLiveStationUsage = (voyageId) =>
  client.get(`/voyages/${voyageId}/live-station-usage`).then((r) => r.data);

// Phase 16 (F8) — voyage-wide menu rotation usage. Shape mirrors the
// live-station-usage map: `{usage: {key: [{day_number, meal,
// restaurant_id, restaurant_name, menu_id, menu_name}, …]}}`. Key
// prefers `menu_id`; falls back to `"name::<lowercased name>"` on
// legacy data that has no `_menu_id` snapshot yet.
export const listVoyageMenuUsage = (voyageId) =>
  client.get(`/voyages/${voyageId}/menu-usage`).then((r) => r.data);

// Phase 16 (F12) — aggregated station library entries.
// type ∈ "all" | "standard" | "live" | "crew_live" | "theme"
export const listStationSourceList = (type = "all") =>
  client.get(`/lib/station-source-list?type=${encodeURIComponent(type)}`)
    .then((r) => r.data);

// Phase 16 (CO1/CO4) — generic library reads for new Phase 16 libs.
// All return [] on any error so the meal-plan editor still renders.
export const listPizzas = () =>
  client.get("/lib/pizzas").then((r) => r.data).catch(() => []);
export const listPizzaSets = () =>
  client.get("/lib/pizza_sets").then((r) => r.data).catch(() => []);
export const listBakerySpecials = () =>
  client.get("/lib/bakery_specials").then((r) => r.data).catch(() => []);
export const listChefsSpecialsGroups = () =>
  client.get("/lib/chefs_specials_groups").then((r) => r.data).catch(() => []);
export const listBakerySpecialsGroups = () =>
  client.get("/lib/bakery_specials_groups").then((r) => r.data).catch(() => []);

// Phase 16 (F13) — Carving catalog + saved bundles. Symmetric mirror of
// Pizza wrappers above. Identical error-tolerance contract.
export const listCarvings = () =>
  client.get("/lib/carvings").then((r) => r.data).catch(() => []);
export const listCarvingSets = () =>
  client.get("/lib/carving_sets").then((r) => r.data).catch(() => []);
